import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { cloneDeep } from "lodash-es";

import { DateTime } from "luxon";
import { tradeAlerts, syncState } from "@/plugins/LocalForagePlugin";
import { tradeAlertsSyncKey } from "@/types/products/product-definitions";
import type {
    Author,
    ContentfulTradeAlert,
    HasMetaData,
    IndustrySector,
    InvestorsIntelligenceSector,
    Product,
    SearchableTicker,
    Tag,
    TradeAlert,
    TradeAlertMetaData
} from "@/types/entities";
import type { TradeAlerts } from "@/types/api/content-models";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";
import { CompositionContext } from "@/services/data/sync-supervisor";

export class TradeAlertsSyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise< number > {
        let itemsUpdated = 0;

        try {
            const syncThresholdInMillis = await syncState.getItem< number >( tradeAlertsSyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() )
            {

                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( tradeAlertsSyncKey, nextSyncThresholdInMillis );

            const t0: number = performance.now();

            // Retrieve & store the latest meta-data for trade alerts
            const allMetadata: HasMetaData[] = await this.refreshMetaDataFromServer( authToken );

            if(0 === allMetadata.length)
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( tradeAlertsSyncKey, nextSyncThresholdInMillis );

                return itemsUpdated;
            }

            const t1: number = performance.now();
            logger.info( `[ trade-alerts-sync-coordinator ] Retrieved & loaded ${ allMetadata.length } trade alert meta records in ${ t1 - t0 } ms` );

            const allMetaDataByIdMap = new Map< string, TradeAlertMetaData >(
                allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
            );

            // Manage all cached trade alerts
            const t2: number = performance.now();

            if ( await tradeAlerts.length() > 0 ) {
                // Iterate over all elements within IDB
                await tradeAlerts.iterate( ( content: TradeAlert, key: string ) => {
                    // If the tradeAlerts in IDB matches one in the meta-data repository
                    const meta: TradeAlertMetaData | undefined = allMetaDataByIdMap.get( key );
                    // If the meta-data collection has the same trade alert content item that shares the same contentful id...
                    if ( typeof meta !== 'undefined' ) {
                        // If the updated date for both the meta-data and the local trade alert are the same...
                        if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                            // Remove it from the list of available meta-data items
                            allMetaDataByIdMap.delete( key );
                        }
                        // Otherwise, the local content item is out of date and should be replaced
                        else {
                            // Delete the content item from the IDB collection
                            tradeAlerts.removeItem( key );
                        }
                    }
                    // Else, the current meta-data no longer contains the same content item and thus it should be removed from IDB
                    else {
                        // Delete the content item from the IDB collection
                        tradeAlerts.removeItem( key );
                    }
                    return;
                } );
            }

            const t3: number = performance.now();
            logger.info( `[ trade-alerts-sync-coordinator ] Aggregated missing trade alerts in ${ t3 - t2 } ms` );

            // Transform metadata map into an array of content ids
            const missingIds = [ ...Array.from( allMetaDataByIdMap.keys() ) ];
            // If any content is missing...
            if ( missingIds.length > 0 ) {
                // Fetch it from the server
                itemsUpdated = await this.refreshContentFromServer( authToken, missingIds );
            }

            const t4: number = performance.now();
            logger.info( `[ trade-alerts-sync-coordinator ] Processing all missing trade alerts took ${ t4 - t0 } ms` );
        }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
        }

        return itemsUpdated;
    }

    private async refreshMetaDataFromServer( authToken: string ): Promise< TradeAlertMetaData[] > {
        let page: number = 0;
        const pageSize: number = 5000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        const metadata: TradeAlertMetaData[] | undefined = [];
        let metaPayload: ContentfulTradeAlert[] | undefined = [];

        do {
            const response =
                await goFetch< TradeAlerts, PersistedQueryPayload >( {
                    endpoint: 'content/graphql',
                    payload: {
                        id: 'cbd5256ca0dcc0879c5601c9aaa8e0bd',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                fromPublishedDateTimeUTC: '2008-01-01'
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Trade alert metadata fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping trade alert metadata pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            metaPayload = response?.data?.tradeAlertCatalog?.trade_alerts;
            if ( metaPayload ) {
                metaPayload.map( ( a: ContentfulTradeAlert ) => {
                    metadata.push( ( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at
                    } ) );
                } );
            }

            // update the page #
            page++;
        } while ( (metaPayload?.length ?? 0) >= pageSize );

        return metadata;
    }

    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise< number > {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();
        let itemsUpdated = 0;

        let page: number = 0;
        const pageSize: number = 500;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        let contentPayload: ContentfulTradeAlert[] | undefined = [];
        do {
            const response =
                await goFetch< TradeAlerts, PersistedQueryPayload >( {
                    endpoint: 'content/graphql',
                    payload: {
                        id: '210dba7500d8d488244bae6c7abe7efc',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                ids: missingContentIds
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Trade alert content fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping trade alert content pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            contentPayload = response?.data?.tradeAlertCatalog?.trade_alerts;
            if ( null != contentPayload ) {
                const allTradeAlerts: TradeAlert[] = [];
                contentPayload.map( ( alert: ContentfulTradeAlert ) => {
                    const tradeAlert: TradeAlert | undefined = composeTradeAlert( alert, this.#compositionContext );
                    if ( typeof tradeAlert !== 'undefined' ) {
                        allTradeAlerts.push( tradeAlert )
                    }
                } );

                // Store the items recently retrieved
                await tradeAlerts.ready();
                try {
                    for ( const origTradeAlert of allTradeAlerts ) {
                        await tradeAlerts.setItem( origTradeAlert.contentfulId, cloneDeep( origTradeAlert ) );
                    }
                } catch ( err: any ) {
                    logger.error( JSON.stringify( err.message ) )
                }

                const t2: number = performance.now();
                logger.info( `Background processing ${ allTradeAlerts.length } trade alert items, mapping & persisting took ${ t2 - t0 } ms` );

                itemsUpdated += allTradeAlerts.length;
            }

            // update the page #
            page++;
        } while ( (contentPayload?.length ?? 0) >= pageSize );

        return itemsUpdated;
    }
}

export function composeTradeAlert( alert: ContentfulTradeAlert, ctx: CompositionContext ): TradeAlert | undefined {
    let hydratedTradeAlert = {} as TradeAlert;
    try {
        // Author
        const author: Author | undefined = ctx.getAuthorById( alert.author_id );

        // Ad Product
        const adProduct: Product | undefined = !!( alert.ad_product_id ) ?
            ctx.getProductById( alert.ad_product_id ) : undefined;

        // Industry Sectors
        const industrySectors: IndustrySector[] = [];
        alert.assoc_ind_sector_ids?.forEach( ( id: string ) => {
            const industrySector: IndustrySector | undefined = ctx.getIndustrySectorById( id );

            if ( industrySector ) {
                industrySectors.push( cloneDeep( industrySector ) );
            }
        } );

        // Products
        const products: Product[] = [];
        alert.assoc_product_ids?.forEach( ( id: string ) => {
            const product: Product | undefined = ctx.getProductById( id );

            if ( product ) {
                products.push( cloneDeep( product ) );
            } else {
                logger.warn( `The given product id '${ id }' was not found to match a known Product content item.` );
            }
        } );

        // Sectors
        const sectors: InvestorsIntelligenceSector[] = [];
        alert.assoc_sector_ids?.forEach( ( id: string ): void => {
            const sector: InvestorsIntelligenceSector | undefined = ctx.getSectorById( id );

            if ( sector ) {
                sectors.push( cloneDeep( sector ) );
            }
        } );

        // Tags
        const tags: Tag[] = [];
        alert.assoc_tag_ids?.forEach( ( id: string ): void => {
            const tag: Tag | undefined = ctx.getTagById( id );

            if ( tag ) {
                tags.push( cloneDeep( tag ) );
            }
        } );

        // Tickers
        const tickers: SearchableTicker[] = [];
        alert.assoc_ticker_ids?.forEach( ( id: string ): void => {
            const ticker: SearchableTicker | undefined = ctx.getTickerById( id );

            if ( ticker ) {
                tickers.push( cloneDeep( ticker ) );
            }
        } );

        hydratedTradeAlert = ( {
            contentfulId: alert.id,
            title: alert.title,
            slug: alert.slug,
            text: alert.body,
            publishedDate: alert.published_date,

            updatedDate: alert.publish_at,

            body2: alert.body2,
            videoLink: alert.video_link,
            isFeatured: alert.is_featured,
            preview: alert.preview,
            thumbnail: alert.thumbnail,

            adProduct: !!( adProduct ) ? cloneDeep( adProduct ) : undefined,
            editorialAd: alert.editorial_ad ?? undefined,

            author: cloneDeep( author ),

            categories: [],
            industrySectors: industrySectors,
            products: products,
            sectors: sectors,
            tags: tags,
            tickers: tickers
        } );

    } catch ( e: any ) {
        logger.error( JSON.stringify( e.message ) );
    }

    return hydratedTradeAlert;
}
